import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Ticket as TicketIcon, UserPlus, Send, Clock, CheckCircle, Download, Share2, RotateCcw } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { QRCodeDisplay } from './QRCodeDisplay';
import { TicketAllocation } from './TicketAllocation';
import { TicketTransferModal } from './TicketTransferModal';
import type { Ticket } from '@/types/tickets';
import jsPDF from 'jspdf';
import QRCodeLib from 'qrcode';
import { useUnifiedAuth } from '@/lib/auth/use-unified-auth';
import { supabase } from '@/lib/supabase/client';

interface TicketListProps {
  tickets: Ticket[];
  experienceTitle: string;
  experienceDate?: string;
  onUpdate?: () => void;
}

export function TicketList({ 
  tickets, 
  experienceTitle, 
  experienceDate,
  onUpdate 
}: TicketListProps) {
  const { user, profile } = useUnifiedAuth();
  const [selectedTicket, setSelectedTicket] = useState<Ticket | null>(null);
  const [showAllocation, setShowAllocation] = useState(false);
  const [showTransfer, setShowTransfer] = useState(false);
  const [expandedTickets, setExpandedTickets] = useState<Set<string>>(()=>{
    const initial = new Set<string>();
    tickets.forEach(t=>{
      if(!t.guest_name && !t.claimed_by){initial.add(t.id);}  // owner tickets
    });
    return initial;
  });

  const toggleTicket = (ticketId: string) => {
    const newExpanded = new Set(expandedTickets);
    if (newExpanded.has(ticketId)) {
      newExpanded.delete(ticketId);
    } else {
      newExpanded.add(ticketId);
    }
    setExpandedTickets(newExpanded);
  };

  // Filter tickets based on their status
  // - Don't show tickets that have been transferred away (transfer_type = 'transfer')
  // - Show allocated tickets with guest info
  // - Show unallocated tickets
  const myTickets = tickets.filter(t => {
    // If ticket has been transferred (not allocated), hide it completely
    if (t.transfer_type === 'transfer') {
      return false;
    }
    // Show all other tickets (unallocated or allocated)
    return true;
  });
  
  const unallocatedTickets = myTickets.filter(t => !t.guest_name && !t.guest_email);
  const allocatedTickets = myTickets.filter(t => t.guest_name || t.guest_email);

  const downloadAllQRs = async () => {
    if (!myTickets.length) return;
    try {
      const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
      
      for (let i = 0; i < myTickets.length; i++) {
        const t = myTickets[i];
        const dataUrl = await QRCodeLib.toDataURL(t.qr_code, { width: 300 });
        if (i > 0) pdf.addPage();
        
        // Set white background
        pdf.setFillColor(255, 255, 255);
        pdf.rect(0, 0, pdf.internal.pageSize.getWidth(), pdf.internal.pageSize.getHeight(), 'F');
        
        // Title - high contrast black text
        pdf.setTextColor(0, 0, 0);
        pdf.setFont('helvetica', 'bold');
        pdf.setFontSize(24);
        pdf.text('Rendezvous Ticket', pdf.internal.pageSize.getWidth() / 2, 20, { align: 'center' });
        
        // QR Code
        const imgWidth = 100;
        const x = (pdf.internal.pageSize.getWidth() - imgWidth) / 2;
        const y = 50;
        pdf.addImage(dataUrl, 'PNG', x, y, imgWidth, imgWidth);
        
        // Ticket holder info
        const detailsY = y + imgWidth + 15;
        const holderName = t.guest_name || profile?.full_name || 'Ticket Holder';
        const holderEmail = t.guest_email || user?.email || '';
        
        pdf.setFont('helvetica', 'bold');
        pdf.setFontSize(14);
        pdf.text(`Ticket Holder: ${holderName}`, pdf.internal.pageSize.getWidth() / 2, detailsY, { align: 'center' });
        
        pdf.setFont('helvetica', 'normal');
        pdf.setFontSize(12);
        pdf.text(holderEmail, pdf.internal.pageSize.getWidth() / 2, detailsY + 7, { align: 'center' });
        
        // Allocation status - dark gray for secondary info
        if (t.transfer_type === 'allocation') {
          pdf.setTextColor(50, 50, 50);
          pdf.setFontSize(11);
          pdf.text('(Allocated ticket)', pdf.internal.pageSize.getWidth() / 2, detailsY + 15, { align: 'center' });
        }
        
        // Experience info - back to black
        pdf.setTextColor(0, 0, 0);
        pdf.setFont('helvetica', 'bold');
        pdf.setFontSize(18);
        pdf.text(experienceTitle, pdf.internal.pageSize.getWidth() / 2, detailsY + 28, { align: 'center' });
        
        if (experienceDate) {
          pdf.setFont('helvetica', 'normal');
          pdf.setFontSize(12);
          pdf.text(experienceDate, pdf.internal.pageSize.getWidth() / 2, detailsY + 36, { align: 'center' });
        }
        
        // Footer
        pdf.setFont('helvetica', 'normal');
        pdf.setFontSize(11);
        pdf.text('Please present this QR code at check-in', pdf.internal.pageSize.getWidth() / 2, detailsY + 50, { align: 'center' });
      }
      pdf.save(`${experienceTitle.replace(/[^a-z0-9]/gi, '-')}-tickets.pdf`);
    } catch (err) {
      console.error('Bulk QR download failed', err);
      alert('Failed to generate PDF');
    }
  };

  return (
    <div className="space-y-4">
      {/* Summary */}
      <div className="bg-earth-800/50 p-4 rounded-lg">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <TicketIcon className="h-5 w-5 text-sand-400" />
            <span className="font-medium text-sand-100">
              {myTickets.length} Active Ticket{myTickets.length !== 1 ? 's' : ''}
            </span>
          </div>
          
          <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
            {myTickets.length > 0 && (
              <Button 
                size="sm" 
                variant="outline" 
                onClick={downloadAllQRs}
                className="w-full sm:w-auto"
              >
                <Download className="h-4 w-4 mr-1" />
                Download QR
              </Button>
            )}
          </div>
        </div>

        {/* Check-in Status */}
        <div className="mt-3 flex items-center gap-4 text-sm">
          <div className="flex items-center gap-1.5">
            <CheckCircle className="h-4 w-4 text-green-400" />
            <span className="text-sand-300">
              {myTickets.filter(t => t.checked_in).length} Checked In
            </span>
          </div>
          <div className="flex items-center gap-1.5">
            <Clock className="h-4 w-4 text-yellow-400" />
            <span className="text-sand-300">
              {myTickets.filter(t => !t.checked_in).length} Pending
            </span>
          </div>
        </div>
      </div>

      {/* Ticket List */}
      <div className="space-y-3">
        {myTickets.map((ticket) => (
          <motion.div
            key={ticket.id}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-earth-800/30 rounded-lg overflow-hidden"
          >
            <div
              onClick={() => toggleTicket(ticket.id)}
              className="w-full p-4 text-left hover:bg-earth-800/50 transition-colors cursor-pointer"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                    ticket.checked_in 
                      ? 'bg-green-500/20 text-green-400' 
                      : 'bg-sand-400/20 text-sand-400'
                  }`}>
                    {ticket.checked_in ? (
                      <CheckCircle className="h-5 w-5" />
                    ) : (
                      <TicketIcon className="h-5 w-5" />
                    )}
                  </div>
                  
                  <div>
                    <p className="font-medium text-sand-100">
                      {ticket.guest_name 
                        ? `${ticket.guest_name}`
                        : profile?.full_name || 'Ticket Holder'}
                    </p>
                    {ticket.guest_name ? (
                      <div>
                        <p className="text-sm text-sand-400">
                          Allocated guest
                        </p>
                        {ticket.guest_email && (
                          <p className="text-xs text-sand-500">{ticket.guest_email}</p>
                        )}
                      </div>
                    ) : (
                      <div>
                        <p className="text-sm text-sand-400">Ticket holder</p>
                        <p className="text-xs text-sand-500">{user?.email || ''}</p>
                      </div>
                    )}
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  {ticket.checked_in && (
                    <span className="text-xs px-2 py-1 bg-green-500/20 text-green-400 rounded-full">
                      Checked In
                    </span>
                  )}
                  
                  {/* Only show transfer button if ticket is not allocated */}
                  {!ticket.guest_email && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedTicket(ticket);
                        setShowTransfer(true);
                      }}
                      className="touch-manipulation active:scale-95 transition-transform"
                    >
                      <Send className="h-3 w-3 sm:mr-1" />
                      <span className="hidden sm:inline">Transfer</span>
                    </Button>
                  )}
                </div>
              </div>
            </div>

            {/* Expanded QR Code */}
            {expandedTickets.has(ticket.id) && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                className="border-t border-earth-700"
              >
                <div className="p-4">
                  <QRCodeDisplay
                    ticket={ticket}
                    experienceTitle={experienceTitle}
                    experienceDate={experienceDate}
                    size={150}
                  />
                  
                  {/* Show allocation status for allocated tickets */}
                  {ticket.guest_email && !ticket.claimed_by && (
                    <div className="mt-4 p-3 bg-yellow-500/10 border border-yellow-500/20 rounded-lg">
                      <p className="text-sm text-yellow-400">
                        Waiting for {ticket.guest_name || 'guest'} to claim their ticket
                      </p>
                      <p className="text-xs text-sand-400 mt-1">
                        Share the link with {ticket.guest_email} to claim
                      </p>
                    </div>
                  )}

                  {/* For allocated tickets (claimed or not), show reclaim button */}
                  {ticket.guest_email && ticket.transfer_type === 'allocation' && (
                    <div className="mt-4 p-3 bg-orange-500/10 border border-orange-500/20 rounded-lg">
                      <p className="text-sm text-orange-400 mb-2">
                        {ticket.claimed_by 
                          ? `This ticket has been claimed by ${ticket.guest_name}`
                          : `This ticket is allocated to ${ticket.guest_name}`
                        }
                      </p>
                      <Button
                        size="sm"
                        variant="outline" 
                        onClick={async (e) => {
                          e.stopPropagation();
                          if (!confirm('Are you sure you want to reclaim this ticket? The current holder will lose access.')) {
                            return;
                          }
                          
                          try {
                            // Generate new QR code and claim token when reclaiming
                            const newQrCode = `qr_${crypto.randomUUID()}`;
                            const newClaimToken = `tk_${Math.random().toString(36).substr(2, 16)}`;
                            
                            const { error } = await supabase
                              .from('tickets')
                              .update({
                                guest_email: null,
                                guest_name: null,
                                claimed_by: null,
                                claimed_at: null,
                                transfer_type: null,
                                qr_code: newQrCode,
                                claim_token: newClaimToken,
                                updated_at: new Date().toISOString()
                              })
                              .eq('id', ticket.id)
                              .eq('booking_id', ticket.booking_id); // Safety check - only owner can reclaim
                              
                            if (error) {
                              console.error('Error reclaiming ticket:', error);
                              alert('Failed to reclaim ticket');
                            } else {
                              onUpdate?.();
                            }
                          } catch (err) {
                            console.error('Error reclaiming ticket:', err);
                            alert('Failed to reclaim ticket');
                          }
                        }}
                        className="w-full"
                      >
                        <RotateCcw className="h-4 w-4 mr-2" />
                        Reclaim Ticket
                      </Button>
                    </div>
                  )}

                  {/* Individual Allocate button for unallocated tickets */}
                  {!ticket.guest_email && !ticket.guest_name && (
                    <div className="mt-4">
                      <Button
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedTicket(ticket);
                          setShowAllocation(true);
                        }}
                        className="w-full"
                      >
                        <UserPlus className="h-4 w-4 mr-2" />
                        Allocate This Ticket to a Guest
                      </Button>
                    </div>
                  )}

                  {ticket.guest_email && !ticket.claimed_by && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={(e)=>{
                        e.stopPropagation(); 
                        const shareLink = `${window.location.origin}/claim/ticket/${ticket.claim_token}`;
                        const message = `Hi ${ticket.guest_name || 'there'}! 🎫\n\nYou have a ticket waiting for you on Rendezvous.\n\nClaim it here:\n${shareLink}\n\nSign in with: ${ticket.guest_email}`;
                        
                        if (navigator.share) {
                          navigator.share({
                            title: 'Your Rendezvous Ticket',
                            text: message
                          }).catch(() => {
                            navigator.clipboard.writeText(shareLink);
                            alert('Link copied to clipboard!');
                          });
                        } else {
                          navigator.clipboard.writeText(shareLink);
                          alert('Link copied to clipboard!');
                        }
                      }}
                    >
                      <Share2 className="h-3 w-3 mr-1"/>
                      Share Link
                    </Button>
                  )}
                </div>
              </motion.div>
            )}
          </motion.div>
        ))}
      </div>

      {/* Modals */}
      {showAllocation && selectedTicket && (
        <TicketAllocation
          tickets={[selectedTicket]}
          onClose={() => {
            setShowAllocation(false);
            setSelectedTicket(null);
          }}
          onComplete={() => {
            setShowAllocation(false);
            setSelectedTicket(null);
            onUpdate?.();
          }}
        />
      )}

      {showTransfer && selectedTicket && (
        <TicketTransferModal
          ticket={selectedTicket}
          onClose={() => {
            setShowTransfer(false);
            setSelectedTicket(null);
          }}
          onComplete={() => {
            setShowTransfer(false);
            setSelectedTicket(null);
            onUpdate?.();
          }}
        />
      )}
    </div>
  );
} 